import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Checkbox, Form } from "../../../components/forms";
import {
  useGetMyProfileQuery,
  useUpdateProfileMutation,
} from "../../../services/userService";
import {
  useLazyGetPostcodesQuery,
  useLazyGetSurgeriesQuery,
} from "../../../services/dataService";
import Select from "react-select";
import { DEBOUNCE_DELAY } from "../../../constants";
import { debounce } from "lodash";
import {
  getDisplayAddress,
  getGPContact,
} from "../../../utils/dataUtils";
import { customSelectStyles } from "../../../constants/styles";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import InfoPopup from "./InfoPopup";
import SurgeryCard from "../../../components/surgeryCard/SurgeryCard";
import { toast } from "react-toastify";
import parseError from "../../../utils/errorUtils";

const ShareGPDetailsForm = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [postCodeOptions, setPostCodeOptions] = useState([]);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isCheckboxChecked, setCheckboxChecked] = useState(false);
  const [gpAddress, setGpAddress] = useState({
    postcode: "",
    line1: "",
    line2: "",
    city: "",
    county: "",
  });
  const [showSurgeries, setShowSurgeries] = useState(false);
  const [surgeriesList, setSurgeriesList] = useState([]);
  const [showPostcodes, setShowPostcodes] = useState(true);
  const [isSurgerySelected, setSurgerySelected] = useState(false);
  const [gpPractice, setGpPractice] = useState("");
  const [manualFill, setManualFill] = useState(false);
  const [gpErrorCount, setGpErrorCount] = useState(0);
  const [consent, setConsent] = useState(false)
  const [disable, setDisable] = useState(true);

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setCheckboxChecked(isChecked);
  };

  const handleCheckConsent = (e) => {
    const isChecked = e.target.checked;
    setConsent(isChecked);
  }

  const handleNavigate = () =>
    navigate("/onboarding/eligibility-flow/additional-info");

  const toggleModal = () => {
    setShowModal((prev) => !prev);
  };
  const validationSchema = Yup.object().shape({});
  const [updateProfile] = useUpdateProfileMutation();
  const { data: profileData } = useGetMyProfileQuery();
  const [getPostCodes, { isFetching: postCodeLoading }] =
    useLazyGetPostcodesQuery();
  const [getSurgeries, { isFetching: surgeryLoading }] =
    useLazyGetSurgeriesQuery();

  const initialValues = {};

  const debouncedChangeHandler = useMemo(
    () => debounce((userInput) => getOptions(userInput), DEBOUNCE_DELAY),
    []
  );

  const getOptions = async (value) => {
    try {
      if (value) {
        const res = await getPostCodes({ partial: value });

        if (res.data) {
          const transformedOptions = res.data.result?.map((optn) => ({
            label: optn,
            value: optn,
          }));

          setPostCodeOptions(transformedOptions);
        } else {
          setPostCodeOptions([]);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    setFirstSubmit(true);
    try {
      const data = {
        ...profileData,
        ...values,
        preferences: {
          ...profileData.preferences,
          accessNHSdetails: JSON.stringify(!!values.accessNHSdetails),
          shareDetailsWithGp: JSON.stringify(!!values.shareDetailsWithGp),
          ...(values.shareDetailsWithGp && {
            gpAddress: JSON.stringify(gpAddress),
            gpPractice: gpPractice
          }),

        },
        regStatus: "PERSONAL_INFORMATION",
      };

      delete data.accessNHSdetails;
      delete data.shareDetailsWithGp;

      setIsSubmitting(true);
      const res = await updateProfile({ values: data });

      if (res.error) {
        setIsSubmitting(false);
        toast.error(parseError(res.error, "Couldn't update profile"));
      } else {
        setIsSubmitting(false);
        !isMobile && toast.success("Successfully saved");
        handleNavigate();
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
    }
  };

  const handlePostalInputChange = (value) => {
    debouncedChangeHandler(value);
  };

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    setIsMobile(isMobile);
  }, []);

  const handleChangeGp = (e) => {
    const { name, value } = e.target;

    setGpAddress((prevAddress) => ({
      ...prevAddress,
      [name]: value,
    }));
  };

  const getSurgeriesList = async (value) => {
    try {
      if (value) {
        const res = await getSurgeries({
          postcode: value.replace(/ /g, ""),
        });

        if (res.data) {
          const surgeries = res.data.value.map((surg) => {
            let address = {
              line1: surg.Address1,
              line2: surg.Address2,
              county: surg.County,
              city: surg.City,
              postcode: surg.Postcode,
            };

            return {
              name: surg.OrganisationName,
              displayAddress: getDisplayAddress(address),
              address: address,
              mobile: getGPContact(surg.Contacts),
            };
          });

          setSurgeriesList(surgeries);
          setShowSurgeries(true);
        }
      }
    } catch (error) { }
  };

  const handleClickOption = (value) => {
    setShowPostcodes(false);
    getSurgeriesList(value);
  };

  const onSelectSurgery = (value) => {
    setGpPractice(value.name);
    setGpAddress({ ...value.address });
  };

  const handleSelectSurgery = (value) => {
    setSurgerySelected(true);
    onSelectSurgery(value);
  };

  const validateTotalErrors = () => {
    const excludeKeys = ["line2", "county"];
    if (firstSubmit) {
      let gpEmptyCount = Object.entries(gpAddress)
        .filter(([key, value]) => !excludeKeys.includes(key) && value === "")
        .length || 0;
      gpEmptyCount =
        gpAddress.postcode === "" ? gpEmptyCount + 1 : gpEmptyCount;
      setGpErrorCount(gpEmptyCount);
    }
  };

  useEffect(() => {
    validateTotalErrors()
    setDisable((isCheckboxChecked && (gpErrorCount !== 0 || gpPractice === "")) || !consent)
  }, [isCheckboxChecked, gpAddress, consent, gpErrorCount, gpPractice])

  return (
    <div className="d-flex flex-column h-100">
      <h4 className="mb-2 fw-bold">Share my GP details</h4>
      <div className="container px-0 d-flex flex-column justify-content-between">
        <div className="column col-md-12">
          <p className="text-gray-1 mb-3">
            Your GP Details will be used to get your health records
            and assess your suitability for medical cannabis treatment.
          </p>
        </div>
      </div>
      <div className="d-flex mt-4 h-100">
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={
            (isCheckboxChecked ? gpErrorCount === 0 : true) && consent &&
            handleSubmit
          }
          enableReinitialize
          className="d-flex w-100 h-100"
          validate={(values) => {
            validateTotalErrors();
          }}
        >
          <div className="d-flex flex-column justify-content-between">
            <div className="container px-0 d-flex flex-column h-100">
              <div className="column col-md-12 col-lg-12 col-xl-9">
                <div className="row mb-4">
                  <div className="col-md-12 mb-4">
                    <Checkbox
                      label="Yes, I agree to allow Treat It doctors access to my medical records for better care."
                      name="accessNHSdetails"
                      className="check-lg"
                      inputClass="rounded"
                      labelClass="ms-3"
                      subtitle={
                        <>
                          Your consent ensures seamless and informed care.{" "}
                          <a
                            onClick={toggleModal}
                            className="text-pink-1 text-decoration-underline cursor-pointer"
                          >
                            Why do we need access to your medical records?
                          </a>
                        </>
                      }
                      subtitleClass="ms-5 flow-gap text-gray-1"
                      isSubtitleOn={true}
                      onChange={handleCheckConsent}
                    />
                    {!consent && firstSubmit && (
                      <label className="text-danger mt-1">
                        Please accept the above consent
                      </label>
                    )}
                  </div>
                  <div className="col-md-12 mb-0 mb-md-0">
                    <Checkbox
                      label="Yes, I'm comfortable sharing my GP details and for the Treat It clinic to reach out to my GP."
                      name="shareDetailsWithGp"
                      className="check-lg"
                      inputClass="rounded"
                      labelClass="ms-3"
                      subtitle="Giving this consent ensures coordinated and comprehensive care for you. Rest assured your details are safe with us. We will never share your details with any 3rd parties."
                      subtitleClass="ms-5 flow-gap text-gray-1"
                      onChange={handleCheckboxChange}
                    />
                  </div>
                </div>
              </div>
              {isCheckboxChecked && (
                <div className="column col-md-12 col-lg-12 col-xl-9 mt-5 mt-md-5">
                  <h4 className="mb-2 fw-bold">Your GP details</h4>
                  <div className="container px-0 d-flex flex-column justify-content-between">
                    <div className="column col-md-12">
                      <p className="text-gray-1 mb-6">
                        Please provide your GP’s details below
                      </p>
                    </div>
                  </div>

                  <div className="row mb-4">
                    <div className="col-md-12 col-lg-6 mb-3 mb-lg-0 position-relative">
                      <div className="d-flex flex-row justify-content-between">
                        <label className="mb-2">
                          GP Post code<span className="text-red-1">*</span>
                        </label>
                        <div className="d-flex flex-row gap-2">
                          <label className="mb-2">
                            Enter details manually
                          </label>
                          <div className="form-check form-switch">
                            <input
                              class="form-check-input cursor-pointer"
                              type="checkbox"
                              role="switch"
                              id="flexSwitchCheckDefault"
                              onChange={(e) =>
                                setManualFill(e.target.checked)
                              }
                            />
                          </div>
                        </div>
                      </div>
                      {!manualFill && (
                        <Select
                          name="gpPostCode"
                          placeholder="Select"
                          styles={customSelectStyles}
                          errorClass="error-text mt-1"
                          options={postCodeOptions}
                          isSearchable
                          onInputChange={handlePostalInputChange}
                          isLoading={postCodeLoading}
                          onChange={(option) => {
                            setSurgerySelected(false);
                            handleClickOption(option.value);
                          }}
                        />
                      )}
                      {manualFill && (
                        <input
                          name="postcode"
                          placeholder="Enter the post code"
                          autoComplete="no"
                          className="p-2 px-3 fs-6 w-100 bg-transparent border card-border rounded custom-placeholder"
                          onChange={handleChangeGp}
                          value={gpAddress.postcode}
                        />
                      )}
                      {gpAddress.postcode === "" && firstSubmit && (
                        <label className="text-danger mt-1">
                          GP post code is required
                        </label>
                      )}
                      {!showPostcodes && surgeryLoading ? (
                        <div className="option-list text-start ps-2 py-1 w-100">
                          <LoadingSpinner loadingText="Searching for surgeries, please wait..." />
                        </div>
                      ) : null}
                      {!showPostcodes && (
                        <div
                          style={{
                            position: "absolute",
                            maxHeight: "300px",
                            overflowY: "auto",
                            width: "93%",
                            backgroundColor: "white",
                            border: !isSurgerySelected
                              ? "1px solid #ddd"
                              : "none",
                            borderRadius: "8px",
                            zIndex: 1050,
                          }}
                          onClickOutside={() => {
                            setShowPostcodes(false);
                            setShowSurgeries(false);
                          }}
                          onMouseEnter={() => setShowSurgeries(true)}
                        >
                          {showSurgeries &&
                            !isSurgerySelected &&
                            surgeriesList.length > 0 &&
                            !surgeryLoading && (
                              <div className="surgery-card-container position-relative py-2 px-1">
                                <div className="surgery-card-list">
                                  {surgeriesList.map((surgery, idx) => (
                                    <div
                                      className={`pb-${idx + 1 === surgeriesList.length
                                        ? "0"
                                        : "2"
                                        } pe-1 ps-1`}
                                      key={idx}
                                    >
                                      <SurgeryCard
                                        title={surgery.name}
                                        phone={surgery.mobile}
                                        address={surgery.displayAddress}
                                        onClick={() =>
                                          handleSelectSurgery(surgery)
                                        }
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                    <div className="col-md-12 col-lg-6">
                      <label className="mb-2">
                        Practice name<span className="text-red-1">*</span>
                      </label>
                      <input
                        name="practiceName"
                        placeholder="Enter your practice name"
                        autoComplete="no"
                        className="p-2 px-3 fs-6 w-100 bg-transparent border card-border rounded custom-placeholder"
                        onChange={(e) => setGpPractice(e.target.value)}
                        value={gpPractice}
                        disabled={!manualFill && !isSurgerySelected}
                      />
                      {gpPractice === "" && firstSubmit && (
                        <label className="text-danger mt-1">
                          Practice name is required
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="row mb-4">
                    <div className="col-md-12 col-lg-6 mb-3 mb-lg-0">
                      <label className="mb-2">
                        Address line 1<span className="text-red-1">*</span>
                      </label>
                      <input
                        name="line1"
                        placeholder="Autofill based on post code"
                        autoComplete="no"
                        className="p-2 px-3 fs-6 w-100 bg-transparent border card-border rounded custom-placeholder"
                        onChange={handleChangeGp}
                        value={gpAddress.line1}
                        disabled={!manualFill && !isSurgerySelected}
                      />
                      {gpAddress.line1 === "" && firstSubmit && (
                        <label className="text-danger mt-1">
                          Address Line 1 is required
                        </label>
                      )}
                    </div>
                    <div className="col-md-12 col-lg-6">
                      <label className="mb-2">
                        Address line 2
                      </label>
                      <input
                        name="line2"
                        placeholder="Enter your address"
                        autoComplete="no"
                        className="p-2 px-3 fs-6 w-100 bg-transparent border card-border rounded custom-placeholder"
                        onChange={handleChangeGp}
                        value={gpAddress.line2}
                        disabled={!manualFill && !isSurgerySelected}
                      />
                    </div>
                  </div>
                  <div className="row mb-4 mb-md-12">
                    <div className="col-md-12 col-lg-6 mb-3 mb-lg-0">
                      <label className="mb-2">
                        Town<span className="text-red-1">*</span>
                      </label>
                      <input
                        name="city"
                        placeholder="Autofill based on post code"
                        autoComplete="no"
                        className="p-2 px-3 fs-6 w-100 bg-transparent border card-border rounded custom-placeholder"
                        onChange={handleChangeGp}
                        value={gpAddress.city}
                        disabled={!manualFill && !isSurgerySelected}
                      />
                      {gpAddress.city === "" && firstSubmit && (
                        <label className="text-danger mt-1">
                          City is required
                        </label>
                      )}
                    </div>
                    <div className="col-md-12 col-lg-6">
                      <label className="mb-2">
                        Country
                      </label>
                      <input
                        name="county"
                        placeholder="Enter your country"
                        autoComplete="no"
                        className="p-2 px-3 fs-6 w-100 bg-transparent border card-border rounded custom-placeholder"
                        onChange={handleChangeGp}
                        value={gpAddress.county}
                        disabled={!manualFill && !isSurgerySelected}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="container px-0 d-flex flex-column h-100">
              <div className="column col-md-12 col-lg-12 col-xl-12 d-flex flex-row justify-content-center justify-content-md-end align-items-end h-100">
                <div className="d-flex flex-column-reverse flex-md-row footer-div px-5 px-md-0 py-4 py-md-0  fixed-single-footer justify-content-md-end">
                  <button
                    className={`bg-primary-600 ${(disable || isSubmitting) ? "opacity-50" : "opacity-100"} text-white py-2 px-5 btn-sm w-md-fit h-fit fw-bold fs-6 border-0 rounded text-nowrap`}
                    type="submit"
                    onClick={() => setFirstSubmit(true)}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <LoadingSpinner loadingText="Continue" />
                    ) : (
                      "Continue"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
      <InfoPopup show={showModal} onClose={toggleModal} />
    </div>
  );
};

export default ShareGPDetailsForm;
